import {
  Archive,
  ChevronDown,
  MessagesSquare,
  Plus,
  Search,
  X
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  groupThreadsForMenu,
  isMyThread,
  isUpdatesThread,
  threadMatchesQuery,
  threadStartedBy
} from "@/features/agents/lib/threadGroups";
import { cn } from "@/lib/utils";

import type { CaseAgentThread } from "../api/caseAgentApi";
import { plainMentions, type TeamMember } from "../mentions";
import {
  DeleteThreadButton,
  memberOf,
  PersonAvatar,
  personLabel,
  ThreadMenuItem,
  ThreadRow,
  type ThreadPeople
} from "./ThreadMenuItem";

type Props = {
  /** Every thread of the scope, archived ones included (the menu has an Archived view). */
  threads: CaseAgentThread[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
  /** Unread mention notifications per thread id (from the notifications feature). */
  unreadByThread?: Record<string, number>;
  /**
   * A single conversations icon opens a searchable, filterable popover (the
   * agent panel's header); the active title and the separate "+" button are
   * not shown.
   */
  compact?: boolean;
  /** The signed-in uid; turns on Mine / All. */
  meUid?: string | null;
  /** The firm's members; turns on Started By and names each row's starter. */
  members?: readonly TeamMember[];
  /** The compact trigger's size, to match the header it sits in. */
  triggerSize?: "icon-xs" | "icon-sm";
  /** Threads homed elsewhere that worked on this case or client; each opens at its home. */
  elsewhere?: CaseAgentThread[];
  onOpenElsewhere?: (thread: CaseAgentThread) => void;
};

type Scope = "mine" | "all";

const UnreadBadge: FC<{ count: number; className?: string }> = ({
  count,
  className
}) => (
  <Badge
    variant="default"
    className={cn("h-4 min-w-4 px-1 text-xs tabular-nums", className)}
    data-testid="threads-unread-badge"
    aria-label={`${count} unread mentions in other conversations`}
  >
    {count > 99 ? "99+" : count}
  </Badge>
);

type MenuProps = Omit<Props, "compact" | "triggerSize" | "disabled"> & {
  onClose: () => void;
  onConfirmDelete: (thread: CaseAgentThread) => void;
};

/** The 360px conversations popover: search, Mine / All, Started By, Archived. */
const ConversationsMenu: FC<MenuProps> = ({
  threads,
  activeId,
  onSelect,
  onNew,
  unreadByThread = {},
  meUid = null,
  members,
  elsewhere = [],
  onOpenElsewhere,
  onClose,
  onConfirmDelete
}) => {
  const people: ThreadPeople | undefined = useMemo(
    () => (members ? { members, meUid } : undefined),
    [members, meUid]
  );
  const [query, setQuery] = useState("");
  const [scopePick, setScope] = useState<Scope | null>(null);
  const [starter, setStarter] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const canMine = Boolean(meUid);
  const mineCount = threads.filter(
    (t) => !t.archived && isMyThread(t, meUid)
  ).length;
  const allCount = threads.filter((t) => !t.archived).length;
  // Mine first, unless the person has nothing there yet.
  const scope: Scope = canMine
    ? scopePick ?? (mineCount > 0 ? "mine" : "all")
    : "all";
  // The client's Updates thread and pinned threads (the case's CaseBlink
  // Workflow thread) are everyone's, so Mine keeps them too.
  const inScope = (t: CaseAgentThread) =>
    scope === "all" ||
    isUpdatesThread(t) ||
    Boolean(t.pinned) ||
    isMyThread(t, meUid);
  const archivedCount = threads.filter((t) => t.archived && inScope(t)).length;

  const base = threads.filter(
    (t) =>
      (archived ? Boolean(t.archived) : !t.archived) &&
      inScope(t) &&
      threadMatchesQuery(t, query)
  );
  const list = base.filter((t) => threadStartedBy(t, starter));
  const groups = archived
    ? [
        {
          id: "archived",
          label: "Archived",
          threads: groupThreadsForMenu(list).flatMap((g) => g.threads)
        }
      ]
    : groupThreadsForMenu(list);
  const flat = groups.flatMap((g) => g.threads);
  const others =
    archived || !onOpenElsewhere
      ? []
      : elsewhere.filter((t) => threadMatchesQuery(t, query));

  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      flat.findIndex((t) => t.id === activeId)
    )
  );
  const cursor = Math.min(activeIndex, Math.max(0, flat.length - 1));
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [cursor]);

  const starterCounts = new Map<string, number>();
  for (const t of base) {
    if (t.created_by)
      starterCounts.set(
        t.created_by,
        (starterCounts.get(t.created_by) ?? 0) + 1
      );
  }
  const countOf = (uid: string) => starterCounts.get(uid) ?? 0;
  const pickable = (members ?? [])
    .filter((m) => m.uid === meUid || countOf(m.uid) > 0)
    .sort((a, b) =>
      a.uid === meUid
        ? -1
        : b.uid === meUid
        ? 1
        : countOf(b.uid) - countOf(a.uid) || a.label.localeCompare(b.label)
    );
  const quiet = (members?.length ?? 0) - pickable.length;
  const starterMember = memberOf(people, starter);
  const starterName = personLabel(people, starter);

  const choose = (t: CaseAgentThread) => {
    onSelect(t.id);
    onClose();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Keys from the Started By menu bubble here through its portal.
    if (!contentRef.current?.contains(e.target as Node)) return;
    if (e.key === "/" && e.target !== inputRef.current) {
      e.preventDefault();
      inputRef.current?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!flat.length) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((cursor + step + flat.length) % flat.length);
    } else if (e.key === "Enter" && flat[cursor]) {
      e.preventDefault();
      choose(flat[cursor]);
    }
  };

  const reset = () => {
    setQuery("");
    setStarter(null);
    setScope("all");
    setActiveIndex(0);
  };
  const emptyCopy = threads.length
    ? `No ${archived ? "archived " : ""}threads${
        starterName
          ? ` started by ${starterName === "You" ? "you" : starterName}`
          : ""
      }${query.trim() ? ` match "${query.trim()}"` : ""}.`
    : "No conversations yet.";

  let row = -1;
  return (
    <div
      ref={contentRef}
      className="flex min-h-0 flex-col"
      onKeyDown={onKeyDown}
      data-testid="threads-popover"
    >
      <div className="flex flex-col gap-2 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Search conversations"
            aria-label="Search conversations"
            className="pr-8 pl-7"
          />
          <span className="absolute top-1/2 right-1.5 -translate-y-1/2">
            {query ? (
              <Button
                variant="ghost"
                size="icon-2xs"
                aria-label="Clear search"
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
              >
                <X />
              </Button>
            ) : (
              <Kbd>/</Kbd>
            )}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-1.5">
          {canMine ? (
            <Tabs
              value={scope}
              onValueChange={(v) => {
                const next = v === "mine" ? "mine" : "all";
                setScope(next);
                setActiveIndex(0);
                if (
                  next === "mine" &&
                  starter &&
                  !threads.some(
                    (t) =>
                      !t.archived &&
                      isMyThread(t, meUid) &&
                      t.created_by === starter
                  )
                )
                  setStarter(null);
              }}
            >
              <TabsList variant="segmented">
                <TabsTrigger value="mine" data-testid="threads-scope-mine">
                  Mine
                  <span className="text-fg-3 tabular-nums">{mineCount}</span>
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="threads-scope-all">
                  All
                  <span className="text-fg-3 tabular-nums">{allCount}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}
          {members ? (
            <div className="flex min-w-0 items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={starter ? "tonal" : "pill"}
                    size="xs"
                    className="min-w-0"
                    data-testid="threads-started-by"
                  >
                    {starterMember ? (
                      <PersonAvatar member={starterMember} />
                    ) : null}
                    <span className="truncate">
                      {starterName ?? "Started By"}
                    </span>
                    {starter ? null : <ChevronDown />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  <DropdownMenuItem
                    onSelect={() => {
                      setStarter(null);
                      setActiveIndex(0);
                    }}
                  >
                    <span className="flex-1">Anyone</span>
                    <span className="text-sm text-fg-3 tabular-nums">
                      {base.length}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {pickable.map((m) => (
                    <DropdownMenuItem
                      key={m.uid}
                      disabled={countOf(m.uid) === 0}
                      onSelect={() => {
                        setStarter(m.uid);
                        setActiveIndex(0);
                      }}
                    >
                      <PersonAvatar member={m} />
                      <span className="flex-1 truncate">
                        {m.uid === meUid ? "You" : m.label}
                      </span>
                      <span className="text-sm text-fg-3 tabular-nums">
                        {countOf(m.uid)}
                      </span>
                    </DropdownMenuItem>
                  ))}
                  {quiet > 0 ? (
                    <DropdownMenuLabel className="text-sm font-normal text-fg-3">
                      {`${quiet} more teammate${
                        quiet === 1 ? " has" : "s have"
                      } no threads in this view.`}
                    </DropdownMenuLabel>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
              {starter ? (
                <Button
                  variant="ghost"
                  size="icon-2xs"
                  aria-label="Clear Started By"
                  onClick={() => setStarter(null)}
                >
                  <X />
                </Button>
              ) : null}
            </div>
          ) : null}
          <span className="flex-1" />
          {archivedCount > 0 || archived ? (
            <Button
              variant={archived ? "tonal" : "ghost"}
              size="xs"
              aria-pressed={archived}
              aria-label={archived ? "Back to Active Threads" : "Show Archived"}
              title={archived ? "Back to Active Threads" : "Show Archived"}
              data-testid="threads-archived"
              onClick={() => {
                setArchived(!archived);
                setActiveIndex(0);
              }}
            >
              <Archive />
              <span className="tabular-nums">{archivedCount}</span>
            </Button>
          ) : null}
        </div>
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-label="Conversations"
        className="max-h-98 overflow-y-auto border-t border-line-2 px-1 pb-1"
      >
        {flat.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-3 py-5 text-center">
            <span className="text-base text-fg-2">{emptyCopy}</span>
            {threads.length > 0 && (query || starter || scope === "mine") ? (
              <Button size="xs" onClick={reset}>
                Search All Threads
              </Button>
            ) : null}
          </div>
        ) : (
          groups
            .filter((g) => g.threads.length)
            .map((g) => (
              <div key={g.id} role="group" aria-label={g.label}>
                <div className="px-2 pt-2.5 pb-1 text-sm font-medium text-fg-3">
                  {g.label}
                </div>
                {g.threads.map((t) => {
                  row += 1;
                  const index = row;
                  const isCursor = index === cursor;
                  return (
                    <div
                      key={t.id}
                      role="option"
                      tabIndex={-1}
                      aria-label={plainMentions(workflowDisplayText(t.title))}
                      aria-selected={t.id === activeId}
                      data-active={isCursor}
                      data-testid="thread-option"
                      className={cn(
                        "group flex cursor-pointer items-start rounded-md px-2 py-1.5",
                        isCursor ? "bg-fill-2" : "hover:bg-fill-1"
                      )}
                      onMouseMove={() => {
                        if (!isCursor) setActiveIndex(index);
                      }}
                      onClick={() => choose(t)}
                    >
                      <ThreadRow
                        thread={t}
                        active={t.id === activeId}
                        unread={unreadByThread[t.id] ?? 0}
                        query={query}
                        people={people}
                        trailing={
                          isUpdatesThread(t) ? null : (
                            <DeleteThreadButton
                              onDelete={() => onConfirmDelete(t)}
                            />
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))
        )}
        {others.length > 0 && onOpenElsewhere ? (
          <div
            role="group"
            aria-label="From Other Conversations"
            data-testid="threads-elsewhere"
          >
            <div className="px-2 pt-2.5 pb-1 text-sm font-medium text-fg-3">
              From Other Conversations
            </div>
            {others.map((t) => (
              <div
                key={t.id}
                role="option"
                tabIndex={-1}
                aria-label={plainMentions(workflowDisplayText(t.title))}
                aria-selected={false}
                data-testid="thread-elsewhere-option"
                className="group flex cursor-pointer items-start rounded-md px-2 py-1.5 hover:bg-fill-1"
                onClick={() => {
                  onClose();
                  onOpenElsewhere(t);
                }}
              >
                <ThreadRow
                  thread={t}
                  active={false}
                  unread={unreadByThread[t.id] ?? 0}
                  query={query}
                  people={people}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5 border-t border-line-2 p-1">
        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            onClose();
            onNew();
          }}
        >
          <Plus /> New Thread
        </Button>
        <span className="flex-1" />
        <span className="flex items-center gap-1 pr-1.5 text-sm text-fg-3">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          <span className="pr-1">Move</span>
          <Kbd>↵</Kbd>
          <span className="pr-1">Open</span>
          <Kbd>Esc</Kbd>
        </span>
      </div>
    </div>
  );
};

export const ThreadSwitcher: FC<Props> = ({
  threads,
  activeId,
  onSelect,
  onNew,
  onDelete,
  disabled,
  unreadByThread = {},
  compact = false,
  meUid,
  members,
  triggerSize = "icon-sm",
  elsewhere,
  onOpenElsewhere
}) => {
  const active = threads.find((t) => t.id === activeId) ?? null;
  const [confirm, setConfirm] = useState<CaseAgentThread | null>(null);
  const [open, setOpen] = useState(false);
  // Mentions waiting in threads other than the open one.
  const otherUnread = Object.entries(unreadByThread).reduce(
    (sum, [id, n]) => (id === activeId ? sum : sum + n),
    0
  );

  return (
    <div className="flex min-w-0 items-center gap-0.5">
      {compact ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size={triggerSize}
              className="relative"
              aria-label="Conversations"
              title="Conversations"
              data-testid="threads-menu"
              disabled={disabled}
            >
              <MessagesSquare />
              {otherUnread > 0 ? (
                <UnreadBadge
                  count={otherUnread}
                  className="absolute -top-0.5 -right-0.5"
                />
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-90 max-w-[calc(100vw-1rem)] gap-0 overflow-hidden p-0"
          >
            {open ? (
              <ConversationsMenu
                threads={threads}
                activeId={activeId}
                onSelect={onSelect}
                onNew={onNew}
                onDelete={onDelete}
                unreadByThread={unreadByThread}
                meUid={meUid}
                members={members}
                elsewhere={elsewhere}
                onOpenElsewhere={onOpenElsewhere}
                onClose={() => setOpen(false)}
                onConfirmDelete={(t) => {
                  setOpen(false);
                  setConfirm(t);
                }}
              />
            ) : null}
          </PopoverContent>
        </Popover>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 min-w-0 max-w-45 gap-1 px-1.5 text-xs"
              disabled={disabled}
            >
              <span className="truncate">
                {active ? active.title : "New chat"}
              </span>
              {otherUnread > 0 ? <UnreadBadge count={otherUnread} /> : null}
              <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Conversations
            </DropdownMenuLabel>
            {threads.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No conversations yet.
              </div>
            ) : null}
            {threads.map((t) => (
              <ThreadMenuItem
                key={t.id}
                thread={t}
                active={t.id === activeId}
                unread={unreadByThread[t.id] ?? 0}
                onSelect={() => onSelect(t.id)}
                onDelete={isUpdatesThread(t) ? undefined : () => setConfirm(t)}
              />
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onNew} className="text-xs">
              <Plus className="size-3.5" /> New conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {compact ? null : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="New conversation"
              onClick={onNew}
              disabled={disabled}
            >
              <Plus className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">New conversation</TooltipContent>
        </Tooltip>
      )}

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirm?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The transcript is removed. Work the agent already did on the case
              stays.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirm) onDelete(confirm.id);
                setConfirm(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
